import { memo, useEffect, useMemo, useRef, useState } from 'react';

import { ChevronsDownUp, ChevronsUpDown, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DATE_ID_FORMAT, DATE_KEY_FORMAT } from '@/helpers';
import { GroupBy } from '@/modules/common';

import { CELL_GROUP_HEIGHT_PX, IDLE_CALLBACK_TIMEOUT_MS } from '../../constants';
import { calculateCellWidth, isCompactCellWidth } from '../../helpers';
import { useVisibleDateWindow } from '../../hooks';
import usePlannerStore from '../../store';
import { RowItem } from '../../types';
import { ScrollableText } from '../assignment/text/scrollable-text';
import { GroupCellShell } from './group-cell-shell';
import { GroupTimelineCell } from './group-timeline-cell';
import { TimelineSpacer } from './timeline-spacer';

interface GroupRowProps {
  row: RowItem;
}

export const GroupRow = memo(({ row }: GroupRowProps) => {
  const { t } = useTranslation(['authenticated.planner']);
  const dates = usePlannerStore(store => store.dates);
  const visibleDateRange = usePlannerStore(store => store.visibleDateRange);
  const zoomLevel = usePlannerStore(store => store.zoomLevel);
  const groupBy = usePlannerStore(store => store.groupBy);
  const isCollapsed = usePlannerStore(store => store.collapsedGroups.has(row.id));
  const toggleGroupCollapse = usePlannerStore(store => store.toggleGroupCollapse);
  const utilizationBasedOn = usePlannerStore(store => store.plannerPreferences.utilizationBasedOn);
  const beginUiTransition = usePlannerStore(store => store.beginUiTransition);
  const endUiTransition = usePlannerStore(store => store.endUiTransition);
  const [isCollapsePending, setIsCollapsePending] = useState(false);
  const collapseRafRef = useRef<number | null>(null);
  const pendingCollapsedValueRef = useRef<boolean | null>(null);

  const isProjects = groupBy === GroupBy.PROJECTS;

  const cellWidth = calculateCellWidth(zoomLevel);
  const isCompact = isCompactCellWidth(cellWidth);
  const { visibleDates, leftSpacerWidth, rightSpacerWidth } = useVisibleDateWindow({
    dates,
    visibleDateRange,
    cellWidth,
  });

  // Phase 1: lightweight shell cells (synchronous)
  const shellCells = useMemo(
    () =>
      visibleDates.map(date => (
        <GroupCellShell
          key={`shell-${date.format(DATE_ID_FORMAT)}`}
          date={date}
          cellWidth={cellWidth}
        />
      )),
    [visibleDates, cellWidth]
  );

  // Phase 2: full cells with utilization data (deferred)
  const [isContentReady, setIsContentReady] = useState(false);
  const idleRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(
        () => {
          if (mountedRef.current) setIsContentReady(true);
        },
        { timeout: IDLE_CALLBACK_TIMEOUT_MS }
      );
      idleRef.current = id;
      return () => window.cancelIdleCallback(id);
    }

    let outer = 0;
    let inner = 0;
    outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        if (mountedRef.current) setIsContentReady(true);
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, []);

  const fullCells = useMemo(
    () =>
      isContentReady
        ? visibleDates.map(date => {
            const dateKey = date.format(DATE_KEY_FORMAT);
            const utilization = row.utilizationByDate?.get(dateKey);
            const hasNoData = row.noDataDates?.has(dateKey) ?? false;
            const utilizationPercentage = utilization?.percent ?? 0;
            const assigned = utilization?.assigned ?? 0;
            const capacity = utilization?.capacity ?? 0;

            return (
              <GroupTimelineCell
                key={`cell-${date.format(DATE_ID_FORMAT)}`}
                isCompact={isCompact}
                utilizationPercentage={utilizationPercentage}
                assigned={assigned}
                capacity={capacity}
                date={date}
                cellWidth={cellWidth}
                utilizationBasedOn={utilizationBasedOn}
                hasNoData={hasNoData}
              />
            );
          })
        : null,
    [
      isContentReady,
      visibleDates,
      row.utilizationByDate,
      row.noDataDates,
      isCompact,
      cellWidth,
      utilizationBasedOn,
    ]
  );

  useEffect(() => {
    if (pendingCollapsedValueRef.current === null) return;

    if (isCollapsed === pendingCollapsedValueRef.current) {
      pendingCollapsedValueRef.current = null;
      setIsCollapsePending(false);
      requestAnimationFrame(() => {
        endUiTransition();
      });
    }
  }, [isCollapsed, endUiTransition]);

  useEffect(() => {
    return () => {
      if (collapseRafRef.current !== null) {
        cancelAnimationFrame(collapseRafRef.current);
      }
      if (pendingCollapsedValueRef.current !== null) {
        pendingCollapsedValueRef.current = null;
        setIsCollapsePending(false);
        endUiTransition();
      }
    };
  }, [endUiTransition]);

  const handleToggleCollapse = () => {
    if (pendingCollapsedValueRef.current !== null) return;

    pendingCollapsedValueRef.current = !isCollapsed;
    setIsCollapsePending(true);
    beginUiTransition();

    collapseRafRef.current = requestAnimationFrame(() => {
      collapseRafRef.current = null;
      toggleGroupCollapse(row.id);
    });
  };

  return (
    <div className="relative flex min-w-fit" style={{ height: CELL_GROUP_HEIGHT_PX }}>
      <div className="sticky left-0 top-0 z-10 flex w-56 min-w-[14rem] items-center justify-between gap-2 border-b border-r bg-accent p-2">
        <div className="flex items-center gap-2 overflow-hidden pl-4 pr-2.5">
          {isProjects && (
            <div className="min-h-1.5 min-w-1.5 rounded" style={{ background: row.color }} />
          )}
          <div className="overflow-hidden">
            <ScrollableText animationDelayMs={100} className="text-sm font-medium">
              {row.title}
            </ScrollableText>
          </div>
        </div>

        <button
          onClick={handleToggleCollapse}
          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded hover:bg-muted"
          aria-label={isCollapsed ? t('expandGroup') : t('collapseGroup')}
          disabled={isCollapsePending}
        >
          {isCollapsePending ? (
            <Loader2 size={16} className="animate-spin" />
          ) : isCollapsed ? (
            <ChevronsDownUp size={16} />
          ) : (
            <ChevronsUpDown size={16} />
          )}
        </button>
      </div>
      <div className="relative flex flex-1 bg-accent">
        <TimelineSpacer widthRem={leftSpacerWidth} height={CELL_GROUP_HEIGHT_PX} />
        {shellCells}
        <TimelineSpacer widthRem={rightSpacerWidth} height={CELL_GROUP_HEIGHT_PX} />
        {fullCells && (
          <div className="absolute inset-0 flex">
            <TimelineSpacer widthRem={leftSpacerWidth} height={CELL_GROUP_HEIGHT_PX} />
            {fullCells}
            <TimelineSpacer widthRem={rightSpacerWidth} height={CELL_GROUP_HEIGHT_PX} />
          </div>
        )}
      </div>
    </div>
  );
});
